import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { cacheRead } from '../api/client';
import { getIdentity } from '../lib/identity';
import { ErrorBoundary } from './ErrorBoundary';
import {
  IconHoy, IconPartidos, IconResumen, IconZonas, IconTimba, IconHistorico, IconUso, IconMenu, IconShare,
} from './NavIcons';

const Shell = styled.div`
  min-height: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding-bottom: 72px; /* deja lugar a la bottom nav */
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accentText};
`;

const Brand = styled.div`
  font-weight: 800;
  letter-spacing: 0.5px;
  font-size: 1.1rem;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShareBtn = styled.button`
  background: rgba(255, 255, 255, 0.16);
  color: inherit;
  border: none;
  border-radius: 999px;
  padding: 7px;
  display: inline-flex;
  align-items: center;
  line-height: 0;
`;

const Actions = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.16);
  color: inherit;
  border: none;
  border-radius: 999px;
  padding: 6px 8px 6px 14px;
`;

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 88px;
  transform: translateX(-50%);
  z-index: 50;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 0.84rem;
  font-weight: 600;
`;

const UserName = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Main = styled.main`
  padding: 12px 16px;
`;

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  a {
    position: relative;
    flex: 1;
    text-align: center;
    padding: 10px 0;
    /* +1 (no +2): son hasta 7 ítems en el ancho fijo del bottom-nav; a +2 los labels largos
       ("Histórico") se parten. Se mantiene chico para que no rompa. */
    font-size: 0.72rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  a.active {
    color: ${({ theme }) => theme.colors.accentInk};
    font-weight: 700;
  }
  a.new { color: ${({ theme }) => theme.colors.boost}; font-weight: 700; }
  a.new.active { color: ${({ theme }) => theme.colors.boost}; }
  a .badge {
    position: absolute;
    top: 3px;
    left: 50%;
    transform: translateX(3px);
    background: ${({ theme }) => theme.colors.boost};
    color: ${({ theme }) => theme.colors.boostText};
    font-size: 0.5rem;
    font-weight: 900;
    letter-spacing: 0.3px;
    padding: 1px 4px;
    border-radius: 6px;
    line-height: 1.1;
  }
  a .ico {
    display: block;
    margin: 0 auto 3px;
    height: 30px;
  }
  a .ico svg { display: block; margin: 0 auto; }
`;

const HOY = { to: '/', Icon: IconHoy, label: 'Hoy', isNew: false };
// Orden: Hoy · Partidos · Resumen · Zonas · Histórico · Timba (última, destacada como novedad).
const BASE_TABS = [
  { to: '/fixtures', Icon: IconPartidos, label: 'Partidos', isNew: false },
  { to: '/resumen', Icon: IconResumen, label: 'Resumen', isNew: false },
  { to: '/groups', Icon: IconZonas, label: 'Zonas', isNew: false },
  { to: '/historico', Icon: IconHistorico, label: 'Histórico', isNew: false },
  { to: '/timba', Icon: IconTimba, label: 'Timba', isNew: true },
];

const USO = { to: '/uso', Icon: IconUso, label: 'Admin', isNew: false };

export function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const nick = useStore((s) => s.nick);
  const adminKey = useStore((s) => s.adminKey);
  const pubShort = useMemo(() => {
    try {
      return getIdentity().pub.slice(0, 8);
    } catch {
      return '';
    }
  }, []);

  // Mientras dura el Mundial: "Hoy" primero. Al terminar (remaining 0): se oculta "Hoy"
  // y "Resumen" pasa a ser principal. "Uso" solo aparece si se desbloqueó el acceso.
  const t = cacheRead<{ remaining: number }>('/tournament');
  const finished = t != null && t.remaining === 0;
  const TABS = [
    ...(finished ? [] : [HOY]),
    ...BASE_TABS,
    ...(adminKey ? [USO] : []),
  ];

  const [toast, setToast] = useState('');
  const share = async () => {
    const url = window.location.origin + (process.env.PUBLIC_URL || '');
    // URL en su propio renglón, debajo del título. Sin campo `url` aparte para no duplicarla.
    const data = { title: 'VamoVamo! — Mundial 2026', text: `¡Seguí el Mundial 2026 con VamoVamo!\n${url}` };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setToast('Enlace copiado');
        setTimeout(() => setToast(''), 1800);
      }
    } catch {
      /* el usuario canceló o no se pudo compartir */
    }
  };

  return (
    <Shell>
      <Header>
        <Brand>VamoVamo!</Brand>
        <Right>
          <ShareBtn onClick={share} aria-label="Compartir">
            <IconShare size={20} />
          </ShareBtn>
          <Actions onClick={() => navigate('/preferencias')} aria-label="Menú y preferencias">
            <UserName>{nick || pubShort || 'Invitado'}</UserName>
            <IconMenu size={22} />
          </Actions>
        </Right>
      </Header>
      {toast && <Toast role="status">{toast}</Toast>}
      <Main>
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </Main>
      <Nav>
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === '/'}
            className={({ isActive }) => `${isActive ? 'active' : ''}${t.isNew ? ' new' : ''}`}
          >
            <span className="ico">
              <t.Icon size={30} />
            </span>
            {t.label}
            {t.isNew && <span className="badge">NEW</span>}
          </NavLink>
        ))}
      </Nav>
    </Shell>
  );
}
